import React, { useState, useEffect } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { FaUpload, FaCheck, FaSpinner } from 'react-icons/fa';

const PDFPageDisplay = ({ pdfFilename, onPageChange }) => {
  const { user } = useAuth();
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState({});

  useEffect(() => {
    if (pdfFilename) {
      loadPDFPages();
    }
  }, [pdfFilename]);

  const loadPDFPages = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`http://localhost:3001/api/get-pdf-pages/${encodeURIComponent(pdfFilename)}`);
      
      if (!response.ok) {
        throw new Error('Failed to load PDF pages');
      }
      
      const data = await response.json();
      setPageImages(data.pageImages);
      console.log('📄 Loaded PDF pages:', data.pageImages.length);
      
    } catch (error) {
      console.error('❌ Error loading PDF pages:', error);
      setError('Failed to load PDF pages');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event, pageNumber) => {
    const file = event.target.files[0];
    if (!file || !user) return;

    try {
      setUploading(true);
      
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `user-submissions/${user.uid}/page_${pageNumber}_${timestamp}.jpg`;
      const storageRef = ref(storage, filename);
      
      // Upload the file
      await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);
      
      // Update the uploaded images state
      setUploadedImages(prev => ({
        ...prev,
        [pageNumber]: downloadURL
      }));
      
      console.log(`✅ Image uploaded for page ${pageNumber}:`, downloadURL);
      
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pageImages.length) {
      setCurrentPage(pageNumber);
      if (onPageChange) {
        onPageChange(pageNumber);
      }
    }
  };

  const currentPageData = pageImages.find(page => page.pageNumber === currentPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 text-purple-500 mx-auto mb-4">
            <FaSpinner size={48} />
          </div>
          <p className="text-purple-700 text-lg">Loading PDF pages...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <button
            onClick={loadPDFPages}
            className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (pageImages.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-purple-700 text-lg">No PDF pages found. Please upload a PDF first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-800 mb-2">📄 PDF Activity Pages</h1>
          <p className="text-purple-600 text-lg">View your PDF pages and upload your artwork</p>
        </div>

        {/* Page Navigation */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 mb-6 shadow-lg">
          <div className="flex justify-between items-center">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>
            
            <div className="text-center">
              <span className="text-purple-700 font-semibold">
                Page {currentPage} of {pageImages.length}
              </span>
            </div>
            
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= pageImages.length}
              className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        </div>

        {/* Current Page Display */}
        {currentPageData && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border-2 border-purple-200">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* PDF Page */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-purple-800">📄 Original PDF Page</h3>
                <div className="border-2 border-purple-200 rounded-lg overflow-hidden">
                  <img
                    src={currentPageData.url}
                    alt={`PDF Page ${currentPage}`}
                    className="w-full h-auto max-h-96 object-contain"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjUwMCIgdmlld0JveD0iMCAwIDQwMCA1MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNTAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjUwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM2QjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiPk5vIGltYWdlIGF2YWlsYWJsZTwvdGV4dD4KPC9zdmc+';
                    }}
                  />
                </div>
              </div>

              {/* User Upload Section */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-purple-800">🎨 Your Artwork</h3>
                
                {uploadedImages[currentPage] ? (
                  <div className="space-y-4">
                    <div className="border-2 border-green-200 rounded-lg overflow-hidden">
                      <img
                        src={uploadedImages[currentPage]}
                        alt={`Your artwork for page ${currentPage}`}
                        className="w-full h-auto max-h-96 object-contain"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                      <FaCheck />
                      <span>Artwork uploaded successfully!</span>
                    </div>
                    <button
                      onClick={() => document.getElementById(`file-upload-${currentPage}`).click()}
                      className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                    >
                      Replace Artwork
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center">
                    <FaUpload className="text-4xl text-purple-400 mx-auto mb-4" />
                    <p className="text-purple-600 mb-4">
                      Upload your artwork for this activity
                    </p>
                    <button
                      onClick={() => document.getElementById(`file-upload-${currentPage}`).click()}
                      disabled={uploading}
                      className="bg-purple-500 text-white px-6 py-3 rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <span className="flex items-center gap-2">
                          <FaSpinner className="animate-spin" />
                          Uploading...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <FaUpload />
                          Upload Artwork
                        </span>
                      )}
                    </button>
                  </div>
                )}
                
                <input
                  id={`file-upload-${currentPage}`}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, currentPage)}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        )}

        {/* Page Thumbnails */}
        <div className="mt-8 bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-lg">
          <h3 className="text-lg font-bold text-purple-800 mb-4">All Pages</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
            {pageImages.map((page) => (
              <button
                key={page.pageNumber}
                onClick={() => goToPage(page.pageNumber)}
                className={`relative border-2 rounded-lg overflow-hidden transition-all ${
                  currentPage === page.pageNumber
                    ? 'border-purple-500 shadow-lg'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <img
                  src={page.url}
                  alt={`Page ${page.pageNumber}`}
                  className="w-full h-20 object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center">
                  {page.pageNumber}
                </div>
                {uploadedImages[page.pageNumber] && (
                  <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
                    <FaCheck className="text-xs" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPageDisplay;
